/**
 * bubu-aviso.js — «Avísame si vuelve» (README paso 8).
 *
 * Escucha bubu:avisame (lo emiten la tarjeta de la tienda y la ficha), abre el modal
 * con la foto y el nombre del Bubu agotado y da de alta el aviso:
 *   1. Klaviyo back-in-stock si hay company id en los ajustes del tema. Se manda desde
 *      aquí, sin recargar; el aviso lo manda el flow «Back in stock» de Klaviyo.
 *   2. Si no, el formulario NATIVO de Shopify, con su recarga, y crea el contacto con la
 *      etiqueta «avisame-<handle>» para poder avisar a mano.
 *
 *      Ojo con el punto 2, que es el que estaba roto: esto se mandaba por fetch a
 *      /contact y Shopify lo rechazaba con un 400 «Missing CAPTCHA token», porque la
 *      tienda tiene la protección antibots activada y solo se la pone a los formularios
 *      que pinta él y se envían de forma normal. Ni un aviso se llegó a apuntar nunca.
 *      Ahora el formulario navega y, al volver, este archivo reabre el modal en
 *      «Apuntado» con el nombre y la foto del Bubu —guardados en sessionStorage, que es
 *      lo único que sobrevive a la recarga—.
 *
 * No guarda nada en localStorage: el estado vive en Klaviyo o en Shopify.
 */
const REVISION = '2024-10-15';
const LLAVE_ENVIO = 'bubu:aviso:enviando';
const capa = () => document.getElementById('bubu-aviso');
const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(v || '').trim());

/* ---------- La protección antibots de Shopify hay que pedirla ----------
   La tienda tiene activada la protección antibots (hCaptcha). Su guardián vive en un
   listener de `submit` del documento y, cuando le llega un formulario que él no ha
   registrado, CORTA el envío y no lo reenvía nunca: el alta se perdía en silencio y el
   cliente se quedaba con el botón en «Apuntándote…». A los formularios que pinta Shopify
   en sus plantillas los registra él solo; a este, que vive dentro de un modal que nace
   oculto, no llegaba.
   Se registra aquí con su propia API pública, `Shopify.captcha.protect`, que es
   justo para esto. A partir de ese momento Shopify le pone su comprobación al
   formulario y el envío nativo pasa como el de cualquier otro formulario suyo.
   Se reintenta porque su script puede cargar después que este archivo, y no se repite:
   al registrarlo, Shopify le deja puesto el atributo data-cptcha. */
function protegeCuandoPueda(form, intentos) {
  if (!form || form.hasAttribute('data-cptcha')) return;
  const api = window.Shopify && window.Shopify.captcha;
  if (api && typeof api.protect === 'function') {
    try {
      api.protect(form, function () {});
    } catch (e) {
      /* Sin protección disponible: el envío nativo sigue su camino igual. */
    }
    return;
  }
  if ((intentos || 0) >= 20) return;
  setTimeout(() => protegeCuandoPueda(form, (intentos || 0) + 1), 300);
}

function pinta(el, datos) {
  const foto = el.querySelector('[data-aviso-foto]');
  foto.innerHTML = datos.img ? `<img src="${datos.img}" alt="" loading="lazy">` : '';
  foto.hidden = !datos.img;
  el.querySelectorAll('[data-aviso-n]').forEach((n) => (n.textContent = datos.nombre || 'este Bubu'));
  el.querySelector('[data-aviso-pend]').hidden = false;
  el.querySelector('[data-aviso-ok]').hidden = true;
  // El formulario es el nativo de Shopify, así que se busca por su id (la etiqueta
  // {% form %} de Liquid no admite atributos con guiones).
  const form = el.querySelector('#bubu-aviso-form') || el.querySelector('[data-aviso-form]');
  if (form) form.reset();
  const btn = el.querySelector('[data-aviso-btn]');
  if (btn) {
    btn.disabled = false;
    btn.textContent = 'Avísame';
  }
}

async function altaKlaviyo(company, variantId, email) {
  const r = await fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(company)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', revision: REVISION },
    body: JSON.stringify({
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          channels: ['EMAIL'],
          profile: { data: { type: 'profile', attributes: { email } } },
        },
        relationships: {
          variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantId}` } },
        },
      },
    }),
  });
  if (!r.ok && r.status !== 202) throw new Error('klaviyo ' + r.status);
  try { window.klaviyo && window.klaviyo.identify({ email: email }); } catch (e) {}
}

/* «Avísame» también capta (05/10/2026): el mismo email entra en la lista de newsletter,
   como el popup. La línea legal del modal lo dice. Si esto fallase, el aviso ya está
   apuntado y no se le enseña ningún error al cliente: lo importante para él es el aviso. */
async function altaLista(company, lista, email) {
  if (!lista) return;
  try {
    await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(company)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: REVISION },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Web · Avísame si vuelve',
            profile: { data: { type: 'profile', attributes: { email } } },
          },
          relationships: { list: { data: { type: 'list', id: lista } } },
        },
      }),
    });
  } catch (e) { /* el aviso ya está apuntado */ }
}

/** Pinta el modal en «Apuntado», con el email y el Bubu que toquen. */
function pintaOk(el, datos, email) {
  const pend = el.querySelector('[data-aviso-pend]');
  const ok = el.querySelector('[data-aviso-ok]');
  if (!pend || !ok) return;
  el.querySelectorAll('[data-aviso-n]').forEach((n) => (n.textContent = (datos && datos.nombre) || 'este Bubu'));
  const foto = el.querySelector('[data-aviso-foto]');
  if (foto) {
    foto.innerHTML = datos && datos.img ? `<img src="${datos.img}" alt="" loading="lazy">` : '';
    foto.hidden = !(datos && datos.img);
  }
  pend.hidden = true;
  ok.hidden = false;
  const e = ok.querySelector('[data-aviso-email]');
  if (e) e.textContent = email || '';
}

document.addEventListener('click', (e) => {
  const b = e.target.closest('[data-bubu-avisame]');
  if (!b) return;
  e.preventDefault();
  document.dispatchEvent(new CustomEvent('bubu:avisame', {
    detail: { variantId: b.dataset.variantId, handle: b.dataset.handle, nombre: b.dataset.nombre, img: b.dataset.img, origen: b },
  }));
});

document.addEventListener('bubu:avisame', (e) => {
  const el = capa();
  if (!el || typeof el.abrir !== 'function') return;
  // Sin Klaviyo el alta va por el formulario nativo, y ese necesita el registro antibots.
  if (!el.dataset.klaviyoCompany) protegeCuandoPueda(el.querySelector('#bubu-aviso-form'), 0);
  el._datos = e.detail || {};
  pinta(el, el._datos);
  el.abrir(e.detail && e.detail.origen);
});

/* En la fase de captura de window, ANTES que la protección antibots de Shopify: se
   engancha sola a este formulario nativo y, si llega primero, manda el alta a /contact y
   a la página «No soy un robot» en vez de a Klaviyo (05/10/2026). Solo se corta la
   propagación en el camino de Klaviyo; el nativo sigue su recorrido de siempre. */
window.addEventListener('submit', async (e) => {
  const form = e.target.closest && e.target.closest('#bubu-aviso-form, [data-aviso-form]');
  if (!form) return;
  const el = capa();
  const input = form.querySelector('input[type="email"]');
  const btn = form.querySelector('[data-aviso-btn]');
  const email = (input.value || '').trim();
  if (!emailOk(email)) {
    e.preventDefault();
    input.focus();
    document.dispatchEvent(new CustomEvent('bubu:cart-error', { detail: { mensaje: 'Escribe un email válido para poder avisarte.' } }));
    return;
  }
  const d = el._datos || {};
  const company = el.dataset.klaviyoCompany;
  if (!company || !d.variantId) {
    /* Formulario NATIVO: se deja pasar con su recarga (ver la cabecera del archivo). La
       etiqueta se escribe aquí porque el modal es uno solo y el Bubu se sabe al pulsar,
       y lo que hace falta para reabrirlo al volver se guarda en sessionStorage. */
    const tags = form.querySelector('[data-aviso-tags]');
    if (tags) tags.value = 'newsletter, avisame' + (d.handle ? '-' + d.handle : '');
    try {
      sessionStorage.setItem(LLAVE_ENVIO, JSON.stringify({ nombre: d.nombre || '', img: d.img || '', handle: d.handle || '', email: email }));
    } catch (_) { /* modo privado */ }
    btn.disabled = true;
    btn.textContent = 'Apuntándote…';
    return;
  }
  e.preventDefault();
  e.stopPropagation();
  btn.disabled = true;
  btn.textContent = 'Apuntándote…';
  try {
    await altaKlaviyo(company, d.variantId, email);
    altaLista(company, el.dataset.klaviyoLista, email);
    pintaOk(el, d, email);
    document.dispatchEvent(new CustomEvent('bubu:avisame-ok', { detail: { ...d, email } }));
  } catch (_) {
    btn.disabled = false;
    btn.textContent = 'Avísame';
    document.dispatchEvent(new CustomEvent('bubu:cart-error', { detail: { mensaje: 'No hemos podido apuntarte. Inténtalo otra vez en un momento.' } }));
  }
}, true);

/* De vuelta del alta nativa: el modal se reabre en «Apuntado» con el Bubu de antes. La
   miga de pan es lo único que cruza la recarga, y solo se hace caso si Shopify confirma
   que el alta se ha posteado —así no se confunde con la newsletter, que usa el mismo
   formulario de cliente—. */
(function alVolver() {
  let guardado = null;
  try {
    if (new URLSearchParams(window.location.search).get('customer_posted') !== 'true') return;
    guardado = sessionStorage.getItem(LLAVE_ENVIO);
    if (guardado) sessionStorage.removeItem(LLAVE_ENVIO);
  } catch (_) { return; }
  if (!guardado) return;
  let d;
  try { d = JSON.parse(guardado); } catch (_) { return; }
  const abre = () => {
    const el = capa();
    if (!el || typeof el.abrir !== 'function') return;
    el._datos = d;
    pintaOk(el, d, d.email);
    el.abrir();
    document.dispatchEvent(new CustomEvent('bubu:avisame-ok', { detail: d }));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => customElements.whenDefined('bubu-capa').then(abre));
  else customElements.whenDefined('bubu-capa').then(abre);
})();
